import { useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import { Trash2 } from "lucide-react";

import toast from "react-hot-toast";
import api from "@/lib/api";

interface AdminUser {
  _id: string;
  name?: string;
  email: string;
  role: string;
  createdAt: string;
}

interface CurrentUser {
  id?: string;
  email?: string;
  role?: string;
}

// Helper to read current user safely from localStorage
function getCurrentUser(): CurrentUser | null {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    return JSON.parse(raw) as CurrentUser;
  } catch {
    return null;
  }
}

// Helper to extract a message from unknown error
function getErrorMessage(error: unknown): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    "message" in (error as { response?: { data?: { message?: string } } })
  ) {
    const errObj = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    return errObj.response?.data?.message ?? errObj.message ?? "Unknown error";
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Unknown error";
}

const UsersPage = () => {
  const currentUser = getCurrentUser();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [otpTarget, setOtpTarget] = useState<AdminUser | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpConfirming, setOtpConfirming] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/users");
      const payload = res?.data?.data ?? res?.data ?? [];
      const list: AdminUser[] = Array.isArray(payload) ? payload : [];
      setUsers(list);
    } catch (error: unknown) {
      console.error(error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Step 1: request delete -> sends OTP to ADMIN_OTP_EMAIL
  const handleRequestDelete = async (user: AdminUser) => {
    if (!window.confirm(`Request deletion for ${user.email}?`)) return;

    setOtpSending(true);
    try {
      await api.post(`/users/${user._id}/delete/request`);
      toast.success(
        "Delete request sent. OTP has been emailed to the super admin."
      );
      setOtpTarget(user);
      setOtpCode("");
    } catch (error: unknown) {
      console.error(error);
      const msg = getErrorMessage(error);
      toast.error(msg || "Failed to start delete flow");
    } finally {
      setOtpSending(false);
    }
  };

  // Step 2: confirm delete with OTP
  const handleConfirmDelete = async () => {
    if (!otpTarget) return;
    if (!otpCode.trim()) {
      toast.error("Please enter the OTP code");
      return;
    }

    setOtpConfirming(true);
    try {
      await api.post(`/users/${otpTarget._id}/delete/confirm`, {
        code: otpCode.trim(),
      });

      toast.success(`User ${otpTarget.email} deleted successfully`);
      setUsers((prev) => prev.filter((u) => u._id !== otpTarget._id));
      setOtpTarget(null);
      setOtpCode("");
    } catch (error: unknown) {
      console.error(error);
      const msg = getErrorMessage(error);
      toast.error(msg || "Failed to confirm deletion");
    } finally {
      setOtpConfirming(false);
    }
  };

  const closeOtpModal = () => {
    if (!otpConfirming) {
      setOtpTarget(null);
      setOtpCode("");
    }
  };

  const handleOtpChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOtpCode(e.target.value);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-1 text-2xl font-bold text-blue-900">System Users</h2>
        <p className="text-sm text-gray-500">
          Manage admin accounts. To delete a user, you must get an OTP
          confirmation sent to the super admin email.
        </p>
      </div>

      <div className="p-4 bg-white rounded shadow">
        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading users…</div>
        ) : users.length === 0 ? (
          <div className="py-8 text-center text-gray-400">No users found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-700">
              <thead className="text-xs text-blue-900 uppercase border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Role</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf =
                    (currentUser?.id && currentUser.id === u._id) ||
                    (currentUser?.email && currentUser.email === u.email);

                  return (
                    <tr key={u._id} className="border-b border-gray-100">
                      <td className="px-4 py-2">{u.name || "—"}</td>
                      <td className="px-4 py-2">{u.email}</td>
                      <td className="px-4 py-2 capitalize">
                        {u.role || "admin"}
                      </td>
                      <td className="px-4 py-2">
                        {u.createdAt
                          ? new Date(u.createdAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {isSelf ? (
                          <span className="text-xs italic text-gray-400">
                            You cannot delete your own account
                          </span>
                        ) : (
                          <button
                            disabled={otpSending}
                            onClick={() => handleRequestDelete(u)}
                            title="Delete user"
                            className="p-2 text-red-600 border border-red-400 rounded hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* OTP modal */}
      {otpTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
          <div className="w-full max-w-sm p-6 bg-white rounded shadow-lg">
            <h3 className="mb-2 text-lg font-semibold text-blue-900">
              Confirm Deletion
            </h3>
            <p className="mb-3 text-sm text-gray-600">
              Enter the OTP that was emailed to the super admin to confirm
              deletion of:
            </p>
            <p className="mb-4 text-sm font-semibold text-gray-800">
              {otpTarget.email}
            </p>

            <input
              type="text"
              value={otpCode}
              onChange={handleOtpChange}
              placeholder="Enter 6-digit OTP"
              className="w-full px-3 py-2 mb-4 text-center border rounded outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={closeOtpModal}
                disabled={otpConfirming}
                className="px-3 py-1 text-sm text-gray-600 border rounded hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={otpConfirming}
                className="px-3 py-1 text-sm text-white bg-red-600 rounded hover:bg-red-500 disabled:opacity-60"
              >
                {otpConfirming ? "Deleting…" : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
